/* Progressive presentation only: native selects remain the source of truth. */
if (!customElements.get('ksl-option-controls')) {
  customElements.define('ksl-option-controls', class extends HTMLElement {
    connectedCallback() {
      this.scope = this.closest('product-info');
      if (!this.scope) return;
      try { this.stones = JSON.parse(this.querySelector('[data-birthstone-images]')?.content.textContent || '{}'); }
      catch { this.stones = {}; }
      this.controller = new AbortController();
      this.observer = new MutationObserver(records => {
        if (records.some(record => record.type === 'childList' || record.target.tagName === 'SELECT')) this.schedule();
      });
      this.observer.observe(this.scope, {childList: true, subtree: true, attributes: true, attributeFilter: ['aria-invalid','aria-describedby','disabled']});
      this.schedule();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.refresh(); });
    }
    kind(label) {
      const name = label.toLowerCase().trim();
      if (/^(?:(?:ring|bracelet|cuff)\s+)?size$|^(?:(?:chain|necklace|bracelet)\s+)?length$/.test(name)) return 'size';
      if (/^(colou?r|finish|material|metal)$/.test(name)) return 'finish';
      return null;
    }
    month(option, label = '') {
      const explicit = Number(option.dataset?.kslMonth);
      if (Number.isInteger(explicit) && explicit >= 1 && explicit <= 12) return explicit;
      const names = ['jan(?:uary)?','feb(?:ruary)?','mar(?:ch)?','apr(?:il)?','may','jun(?:e)?','jul(?:y)?','aug(?:ust)?','sep(?:t(?:ember)?)?','oct(?:ober)?','nov(?:ember)?','dec(?:ember)?'];
      const text = `${option.textContent || ''} ${option.value || ''}`.toLowerCase();
      const matches = names.flatMap((name,index) => new RegExp(`\\b${name}\\b`, 'i').test(text) ? [index + 1] : []);
      if (matches.length === 1) return matches[0];
      if (matches.length) return null; // Combined month choices retain their original presentation.
      if (/month|birth\s*stone|tháng/i.test(label) && !/quantity|count|number|số lượng/i.test(label)) {
        const numeric = (option.textContent || '').trim().match(/^(?:month\s*|tháng\s*)?0?([1-9]|1[0-2])$/i);
        if (numeric) return Number(numeric[1]);
      }
      return null;
    }
    stoneImage(month, option) {
      const url = option?.dataset.kslStoneImage || this.stones[month];
      if (!url) return null;
      const image = document.createElement('img');
      image.src = url; image.alt = ''; image.width = 72; image.height = 72;
      image.loading = 'lazy'; image.decoding = 'async'; image.setAttribute('aria-hidden','true');
      image.addEventListener('error', () => { image.hidden = true; }, {once:true});
      return image;
    }
    color(value, select) {
      const option = [...select.options].find(o => o.value === value);
      if (option?.dataset.optionSwatchValue) return option.dataset.optionSwatchValue;
      const name = option?.textContent.toLowerCase() || '';
      if (/rose\s*gold/.test(name)) return '#d3a28e';
      if (/white\s*gold|silver|platinum|rhodium/.test(name)) return '#bdbdbb';
      if (/gold/.test(name)) return '#d5b66a';
      return null;
    }
    syncToNative(select, kind) {
      if (select.closest('variant-selects')) return false;
      const canonical = value => value.toLowerCase().match(/[\p{L}\p{N}]+/gu)?.sort().join(' ') || '';
      const choices = [...select.options].filter(o => o.value !== '');
      for (const native of this.scope.querySelectorAll('variant-selects select')) {
        const label = this.scope.querySelector(`label[for="${CSS.escape(native.id)}"]`);
        if (this.kind(label?.textContent || '') !== kind) continue;
        const nativeNames = new Set([...native.options].map(o => canonical(o.textContent)));
        if (!choices.length || !choices.every(o => nativeNames.has(canonical(o.textContent)))) continue;
        const match = choices.find(o => canonical(o.textContent) === canonical(native.selectedOptions[0]?.textContent || ''));
        if (!match) continue;
        const field = select.closest('.ksl-personalization__field, .ksl-fixed__field');
        if (!field) continue;
        field.hidden = true;
        if (select.value !== match.value) {
          select.value = match.value;
          select.dispatchEvent(new Event('change', {bubbles:true}));
        }
        return true;
      }
      return false;
    }
    refresh() {
      const selectors = 'variant-selects .product-form__input--dropdown select, .ksl-fixed__options select, ksl-personalization select, ksl-reference-picker select, ksl-customization select';
      for (const select of this.scope.querySelectorAll(selectors)) {
        if (!this.scope.contains(select)) continue;
        const label = this.scope.querySelector(`label[for="${CSS.escape(select.id)}"]`) || select.labels?.[0];
        let kind = this.kind(label?.textContent || '');
        const choices = [...select.options].filter(o => o.value !== '');
        if (!kind && choices.length && choices.every(o => this.month(o,label?.textContent))) {
          if (choices.some(o => o.dataset.kslStoneImage || this.stones[this.month(o,label?.textContent)])) kind = 'birthstone';
        }
        if (kind && this.syncToNative(select, kind)) continue;
        if (select.dataset.kslEnhanced) {
          const options = select.closest('.ksl-choice')?.querySelector('[role="radiogroup"]');
          if (options) {
            options.setAttribute('aria-invalid', select.getAttribute('aria-invalid') || 'false');
            if (select.getAttribute('aria-describedby')) options.setAttribute('aria-describedby', select.getAttribute('aria-describedby'));
            for (const button of options.querySelectorAll('[role="radio"]')) button.disabled = select.disabled || [...select.options].find(o => o.value === button.dataset.value)?.disabled;
          }
          continue;
        }
        if (!kind || select.multiple) continue;
        if (!choices.length) continue;
        this.enhance(select, label, kind, choices);
      }
      this.enhanceNativeMonths();
      const monthChoice = [...this.scope.querySelectorAll('.ksl-choice--birthstone, .ksl-native-months')].find(group => !group.closest('[hidden]'));
      if (monthChoice && !monthChoice.closest('ksl-reference-picker') && this.dataset.stoneNote && !this.scope.querySelector('[data-ksl-stone-note]')) {
        const note = document.createElement('p'); note.dataset.kslStoneNote = ''; note.className = 'ksl-choice__stone-note'; note.textContent = this.dataset.stoneNote;
        monthChoice.after(note);
      }
      this.placeGuide();
      if (this.pendingFocus) {
        const select = this.scope.querySelector(`#${CSS.escape(this.pendingFocus)}`);
        const group = select?.closest('.ksl-choice');
        if (group && select !== this.pendingSelect) {
          if (document.activeElement === document.body || !this.scope.contains(document.activeElement)) {
            (group.querySelector('[aria-checked="true"]') || group.querySelector('[role="radio"]'))?.focus({preventScroll:true});
          }
          this.pendingFocus = null;
        }
      }
    }
    enhance(select, label, kind, choices) {
      const group = document.createElement('div');
      group.className = `ksl-choice ksl-choice--${kind}`;
      select.parentElement.insertBefore(group, select);
      const heading = document.createElement('div');
      heading.className = 'ksl-choice__heading';
      const title = document.createElement('span');
      title.id = `${select.id}-choice-title`;
      const labelText = label?.textContent.trim() || select.getAttribute('aria-label') || 'Birthstone';
      title.textContent = kind === 'finish' && /^colou?r$/i.test(labelText) ? this.dataset.finishLabel || 'Finish' : labelText;
      const selected = document.createElement('span');
      selected.className = 'ksl-choice__selected';
      heading.append(title, selected);
      const options = document.createElement('div');
      options.className = 'ksl-choice__values';
      options.setAttribute('role', 'radiogroup');
      options.setAttribute('aria-labelledby', title.id);
      const buttons = choices.map(option => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-label', option.textContent.trim());
        button.dataset.value = option.value;
        button.disabled = option.disabled;
        const color = kind === 'finish' && this.color(option.value, select);
        if (kind === 'birthstone') {
          button.className = 'ksl-choice__stone';
          const image = this.stoneImage(this.month(option,label?.textContent), option);
          const caption = document.createElement('span'); caption.textContent = option.textContent.trim();
          if (image) button.append(image);
          button.append(caption);
        } else if (color) {
          button.className = 'ksl-choice__swatch';
          const chip = document.createElement('span');
          chip.style.background = color;
          chip.setAttribute('aria-hidden', 'true');
          button.append(chip);
          button.title = option.textContent.trim();
          const compound = option.textContent.trim().split(/\s+-\s+/);
          if (compound.length > 1) {
            const caption = document.createElement('span');
            caption.className = 'ksl-choice__caption';
            caption.textContent = compound.slice(0, -1).join(' - ');
            button.classList.add('ksl-choice__swatch--named');
            button.append(caption);
          }
        } else button.textContent = option.textContent.trim();
        if (option.dataset.kslUnavailable === 'true') button.classList.add('is-unavailable');
        button.addEventListener('click', () => {
          this.pendingFocus = select.id;
          this.pendingSelect = select;
          select.value = option.value;
          if (kind === 'birthstone') select.setAttribute('aria-invalid','false');
          sync();
          select.dispatchEvent(new Event('change', {bubbles:true}));
          if (select.isConnected && !select.closest('variant-selects')) this.pendingFocus = null;
          this.schedule();
        }, {signal: this.controller.signal});
        options.append(button);
        return button;
      });
      const sync = () => {
        buttons.forEach(button => {
          const checked = button.dataset.value === select.value;
          button.disabled = select.disabled || choices.find(o => o.value === button.dataset.value)?.disabled;
          button.setAttribute('aria-checked', String(checked));
          button.tabIndex = checked ? 0 : -1;
        });
        if (!buttons.some(b => b.tabIndex === 0)) buttons.find(b => !b.disabled)?.setAttribute('tabindex', '0');
        selected.textContent = ['finish','birthstone'].includes(kind) && select.value ? select.selectedOptions[0]?.textContent.trim() || '' : '';
      };
      options.addEventListener('keydown', event => {
        if (!['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End'].includes(event.key)) return;
        event.preventDefault();
        const available = buttons.filter(b => !b.disabled);
        if (!available.length) return;
        const current = available.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? available.length - 1 :
          (current + (['ArrowLeft','ArrowUp'].includes(event.key) ? -1 : 1) + available.length) % available.length;
        available[next].focus(); available[next].click();
      }, {signal: this.controller.signal});
      const hadFocus = document.activeElement === select;
      group.append(heading, options, select);
      select.dataset.kslEnhanced = kind;
      select.hidden = true;
      label?.classList.add('visually-hidden');
      select.closest('.select')?.classList.add('ksl-choice-select-wrapper');
      select.addEventListener('change', sync, {signal: this.controller.signal});
      if (kind === 'birthstone') select.addEventListener('invalid', event => {
        event.preventDefault(); select.setAttribute('aria-invalid','true');
        options.setAttribute('aria-invalid','true');
        buttons.find(button => !button.disabled)?.focus();
      }, {signal:this.controller.signal});
      sync();
      if (hadFocus) (buttons.find(b => b.tabIndex === 0))?.focus({preventScroll:true});
    }
    enhanceNativeMonths() {
      for (const field of this.scope.querySelectorAll('variant-selects fieldset.product-form__input--pill')) {
        if (field.dataset.kslMonthEnhanced) continue;
        const inputs = [...field.querySelectorAll('input[type=radio]')];
        const choices = inputs.map(input => ({input,label:field.querySelector(`label[for="${CSS.escape(input.id)}"]`),month:this.month(input,field.querySelector('legend')?.textContent)}));
        if (!choices.length || choices.some(c => !c.label || !c.month) || !choices.some(c => c.input.dataset.kslStoneImage || this.stones[c.month])) continue;
        field.classList.add('ksl-native-months'); field.dataset.kslMonthEnhanced = 'true';
        for (const {input,label,month} of choices) {
          const image = this.stoneImage(month, input);
          if (image) label.prepend(image);
        }
      }
    }
    placeGuide() {
      const heading = this.scope.querySelector('.ksl-choice--size .ksl-choice__heading');
      const guide = this.scope.querySelector('ksl-size-guide');
      if (!heading || !guide) return;
      if (!heading.querySelector('[data-ksl-size-link]')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.kslSizeLink = '';
        button.className = 'ksl-choice__guide';
        button.textContent = guide.querySelector('summary')?.textContent.trim() || 'Size guide';
        button.setAttribute('aria-haspopup', 'dialog');
        button.addEventListener('click', () => {
          guide.returnFocus = button;
          guide.querySelector('summary')?.click();
        }, {signal:this.controller.signal});
        heading.append(button);
      }
      guide.dataset.inlineGuide = '';
    }
  });
}
